import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { getProjectById, updateProject, deleteProject } from '../services/projects';
import {
  generateSlideData,
  suggestSlideOutline,
  suggestSlideOutlineEdits,
  suggestTemplateSlideNumber,
  suggestTemplateSlideNumbers,
  suggestSlideElementValue,
  recommendSlideDescription,
  batchRecommendSlideDescriptions,
} from '../services/ai';
import { Project, ProjectUpdate, Slide, JsonPatchOperation, SuggestedOutlineEditsResult } from '../types';
import { useLang } from '../contexts/langContext';

export const useProject = (projectId?: string) => {
  const { lang } = useLang();
  const [project, setProject] = useState<Project | null>(null);
  const [suggestedOutline, setSuggestedOutline] = useState<Slide[]>([]);
  const [suggestedOutlineEdits, setSuggestedOutlineEdits] = useState<JsonPatchOperation[]>([]); // Array of JSON Patch operations (RFC 6902)
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false);
  const [isGeneratingSlideDescription, setIsGeneratingSlideDescription] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (projectId) {
      fetchProject(projectId);
    }
  }, [projectId]);

  const fetchProject = async (id: string) => {
    try {
      const fetchedProject = await getProjectById(id);
      setProject(fetchedProject);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const handleUpdateProject = async (projectData: ProjectUpdate) => {
    if (project) {
      try {
        const updatedProject = await updateProject(project._id, projectData);
        fetchProject(updatedProject._id);
      } catch (error) {
        console.error('Error updating project:', error);
        throw error;
      }
    }
  };

  const handleRecommendSlideDescription = async (slideIndex: number) => {
    if (!project) {
      throw new Error('Project not found');
    }

    setIsGeneratingSlideDescription(true);
    try {
      const recommendation = await recommendSlideDescription(project._id, slideIndex);
      return recommendation;
    } catch (error) {
      console.error('Error generating slide description recommendation:', error);
      throw error;
    } finally {
      setIsGeneratingSlideDescription(false);
    }
  };

  const handleUpdateSlides = async (updatedSlides: Slide[]) => {
    if (project) {
      try {
        const updatedProject = await updateProject(project._id, { slides: updatedSlides });
        setProject((prevProject) =>
          prevProject
            ? {
                ...prevProject,
                slides: updatedSlides,
              }
            : null
        );
      } catch (error) {
        console.error('Error updating slides:', error);
      }
    }
  };

  const handleDeleteProject = async () => {
    if (project) {
      try {
        await deleteProject(project._id);
        navigate('/projects');
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleSuggestOutline = async (instruction?: string, fileIds?: string[]) => {
    if (project) {
      try {
        const outline = await suggestSlideOutline(project._id, { instruction, fileIds });
        setSuggestedOutline(outline);
        return outline;
      } catch (error) {
        console.error('Error suggesting outline:', error);
        throw error;
      }
    }
  };

  const handleSuggestOutlineEdits = async (instruction: string, fileIds?: string[]): Promise<SuggestedOutlineEditsResult> => {
    if (!project) {
      throw new Error('Project not found');
    }

    try {
      const result = await suggestSlideOutlineEdits(project._id, instruction, fileIds);
      setSuggestedOutlineEdits(result.edits);
      return result;
    } catch (error) {
      console.error('Error suggesting outline edits:', error);
      throw error;
    }
  };

  const handleSuggestPptxSlideNumber = async (slideIndex: number): Promise<{ slideNumber: number | null | undefined; reason?: string }> => {
    if (project) {
      try {
        const result = await suggestTemplateSlideNumber(project._id, slideIndex);
        console.log('AI result:', result);
        return {
          slideNumber: typeof result === 'object' && 'slideNumber' in result ? result.slideNumber : null,
          reason: typeof result === 'object' && 'reason' in result ? result.reason : undefined,
        };
      } catch (error) {
        console.error('Error suggesting template:', error);
        throw error;
      }
    }
    return { slideNumber: undefined };
  };

  const handleSuggestPptxSlideNumbers = async (slideIndexes: number[]): Promise<number[]> => {
    if (!project) {
      throw new Error('Project not found');
    }

    try {
      const result = await suggestTemplateSlideNumbers(project._id, slideIndexes);
      console.log('AI batch result:', result);
      return result;
    } catch (error) {
      console.error('Error suggesting template slide numbers:', error);
      throw error;
    }
  };

  const handleGenerateSlideData = async (slideIndex: number, instruction?: string, fileIds?: string[]) => {
    if (project) {
      try {
        const slideData = await generateSlideData(project._id, slideIndex, instruction, fileIds);
        return slideData;
      } catch (error) {
        console.error('Error generating slide data:', error);
        throw error;
      }
    }
  };

  const handleSuggestSlideElementValue = async (
    slideIndex: number,
    elementName: string,
    elementType: 'text' | 'picture' | 'table' | 'chart',
    instruction?: string,
    fileIds?: string[]
  ) => {
    if (!project) {
      throw new Error('Project not found');
    }

    try {
      const result = await suggestSlideElementValue(project._id, slideIndex, elementName, elementType, instruction, fileIds);
      console.log('AI result:', result);
      return result;
    } catch (error) {
      console.error('Error suggesting slide field value:', error);
      throw error;
    }
  };

  return {
    project,
    suggestedOutline,
    suggestedOutlineEdits,
    isGeneratingOutline,
    isGeneratingSlideDescription,
    handleUpdateProject,
    handleUpdateSlides,
    handleDeleteProject,
    handleSuggestOutline,
    handleSuggestOutlineEdits,
    handleSuggestPptxSlideNumber,
    handleSuggestPptxSlideNumbers,
    handleGenerateSlideData,
    handleSuggestSlideElementValue,
    handleRecommendSlideDescription,
  };
};
